import React from 'react';
import {ATTRIBUTES_BY_ID, SKILLS_BY_ID, localized} from '../data/traits';
import {DISCIPLINES_BY_ID} from '../data/disciplines';

const range = (count) => Array.from({length: count}, (_, index) => index);

export const SectionTitle = ({children}) => (
  <h2 className="vtmSectionTitle">{children}</h2>
);

/** Points ●●●○○ */
export const Dots = ({value = 0, max = 5, label}) => (
  <span className="vtmDots" role="img" aria-label={`${label ? `${label} : ` : ''}${value} / ${max}`}>
    {range(max).map(index => (
      <span key={index} className={`vtmDot${index < value ? ' on' : ''}`} aria-hidden="true">●</span>
    ))}
  </span>
);

const SlashIcon = () => (
  <svg viewBox="0 0 20 20" aria-hidden="true"><line x1="3" y1="17" x2="17" y2="3" /></svg>
);

const CrossIcon = () => (
  <svg viewBox="0 0 20 20" aria-hidden="true"><line x1="3" y1="17" x2="17" y2="3" /><line x1="3" y1="3" x2="17" y2="17" /></svg>
);

/** Case de jauge : bouton si la fiche est modifiable, simple décor sinon. */
const Box = ({className, onClick, label, children}) => (onClick ? (
  <button type="button" className={`${className} editable`} onClick={onClick} aria-label={label}>{children}</button>
) : (
  <span className={className} aria-hidden="true">{children}</span>
));

/**
 * Jauge de Santé ou de Volonté : aggravés d'abord, puis superficiels, puis cases libres.
 * @param {Function} [onBoxClick] (index) => void, rend les cases cliquables
 */
export const DamageTrack = ({max, track, label, onBoxClick, boxLabel}) => (
  <div className="vtmTrack" role={onBoxClick ? 'group' : 'img'} aria-label={`${label} : ${track.aggravated} / ${track.superficial} / ${max}`}>
    {range(max).map(index => {
      const aggravated = index < track.aggravated;
      const superficial = !aggravated && index < track.aggravated + track.superficial;
      return (
        <Box
          key={index}
          className={`vtmBox${aggravated ? ' aggravated' : ''}${superficial ? ' superficial' : ''}`}
          onClick={onBoxClick && (() => onBoxClick(index))}
          label={boxLabel && boxLabel(index)}
        >
          {aggravated && <CrossIcon />}
          {superficial && <SlashIcon />}
        </Box>
      );
    })}
  </div>
);

/** Humanité : cases pleines à gauche, souillures à droite. */
export const HumanityTrack = ({humanity, stains, onBoxClick, boxLabel}) => (
  <div className="vtmTrack small" role={onBoxClick ? 'group' : 'img'} aria-label={`${humanity} / 10, ${stains}`}>
    {range(10).map(index => {
      const filled = index < humanity;
      const stained = !filled && index >= 10 - stains;
      return (
        <Box
          key={index}
          className={`vtmBox${filled ? ' filled' : ''}${stained ? ' stained' : ''}`}
          onClick={onBoxClick && (() => onBoxClick(index))}
          label={boxLabel && boxLabel(index)}
        >
          {stained && <SlashIcon />}
        </Box>
      );
    })}
  </div>
);

const DropIcon = ({on}) => (
  <svg className={`vtmDrop${on ? ' on' : ''}`} viewBox="0 0 28 36" aria-hidden="true">
    <path d="M14 2 C 14 2, 3 16, 3 23 a 11 11 0 0 0 22 0 C 25 16, 14 2, 14 2 Z" />
  </svg>
);

export const HungerDrops = ({hunger, onDropClick, dropLabel}) => (
  <div className="vtmHunger" role={onDropClick ? 'group' : 'img'} aria-label={`${hunger} / 5`}>
    {range(5).map(index => (onDropClick ? (
      <button key={index} type="button" className="vtmDropButton" onClick={() => onDropClick(index)} aria-label={dropLabel && dropLabel(index)}>
        <DropIcon on={index < hunger} />
      </button>
    ) : (
      <DropIcon key={index} on={index < hunger} />
    )))}
  </div>
);

/** Petits boutons −/+ autour d'une valeur. */
export const MiniStepper = ({label, value, onChange, decrementLabel, incrementLabel}) => (
  <div className="vtmMiniStepper">
    <span className="vtmMiniStepperLabel">{label}</span>
    <button type="button" onClick={() => onChange(-1)} aria-label={decrementLabel}>−</button>
    <output>{value}</output>
    <button type="button" onClick={() => onChange(1)} aria-label={incrementLabel}>+</button>
  </div>
);

// Historiques cités dans certaines réserves (ex : Osiris chasse avec Intimidation + Gloire)
const OTHER_TRAITS = {fame: {name: {fr: 'Gloire', en: 'Fame'}}};

const traitName = (id, lng) => localized(
  (ATTRIBUTES_BY_ID[id] || SKILLS_BY_ID[id] || DISCIPLINES_BY_ID[id] || OTHER_TRAITS[id] || {}).name,
  lng,
) || id;

/** [['wits', 'resolve'], 'auspex'] → « Astuce / Résolution + Auspex » */
export const formatPool = (pool, lng) => (pool || [])
  .map(part => (Array.isArray(part) ? part.map(id => traitName(id, lng)).join(' / ') : traitName(part, lng)))
  .join(' + ');
